import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback, useEffect } from 'react';
import { Button, Menu, MenuItem, Box, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import './styles.css';
import { useDAppState } from '../DAppStateProvider';
import { DEFAULT_NETWORK_IMAGES } from '../network-images';
import { shortenAddress } from '../../utils/common';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
const ChainIcon = styled.img `
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #f5f5f5;

  &:hover {
    transform: scale(1.05);
    transition: transform 0.2s ease;
  }
`;
const CV_BUTTON_SX = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    bgcolor: 'transparent',
    color: '#ece8e0',
    border: '1px solid rgba(255, 106, 26, 0.45)',
    borderRadius: 0,
    minWidth: '148px',
    height: '40px',
    px: 1.5,
    textTransform: 'none',
    fontFamily: "'Chakra Petch', sans-serif",
    fontWeight: 600,
    letterSpacing: '0.04em',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: 'rgba(255, 106, 26, 0.12)',
        borderColor: '#ff6a1a',
    },
};
const DEFAULT_BUTTON_SX = {
    flexDirection: 'row',
    alignItems: 'center',
    bgcolor: 'rgb(29, 197, 71)',
    color: 'white',
    '&:hover': { bgcolor: 'rgb(14, 219, 123)' },
    width: '150px',
    height: '50px',
};
export const MultichainConnectButton = ({ toast: toastApi, variant = 'default', onNavigateToMarketplace, onNavigateToCollection, ...props }) => {
    const { activeState, walletConnection, ConnectionState, setIsWalletModalOpen, handleDisconnect, switchNetwork, switchableNetwork, installedWallets, } = useDAppState();
    const { chain: selectedNetwork } = useParams();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClose = () => setAnchorEl(null);
    // Redirect to marketplace if connected network doesn't match the page network
    useEffect(() => {
        if (selectedNetwork &&
            walletConnection.network &&
            walletConnection.network !== selectedNetwork &&
            walletConnection.connectionState === ConnectionState.Connected &&
            walletConnection.wallet) {
            if (onNavigateToMarketplace)
                onNavigateToMarketplace();
            else
                navigate('/marketplace');
        }
    }, [selectedNetwork, walletConnection]);
    const handleButtonClick = (event) => {
        if (walletConnection.connectionState === ConnectionState.Connected)
            setAnchorEl(event.currentTarget);
        if (walletConnection.connectionState !== ConnectionState.Connected) {
            setIsWalletModalOpen(true);
        }
    };
    const switchNetworkClicked = (newNetwork) => {
        if (selectedNetwork) {
            if (onNavigateToCollection)
                onNavigateToCollection(newNetwork);
            else
                navigate(`/collection/${newNetwork}`);
        }
        switchNetwork(newNetwork);
        handleClose();
    };
    const copyAddress = useCallback(async () => {
        if (!walletConnection.address)
            return;
        try {
            await navigator.clipboard.writeText(walletConnection.address);
            toastApi?.success('Address copied to clipboard');
            handleClose();
        }
        catch (err) {
            toastApi?.error('Failed to copy address');
        }
    }, [walletConnection]);
    return (_jsx(_Fragment, { children: _jsxs(Box, { sx: { position: 'relative', zIndex: 1000 }, className: props.className, children: [_jsx(Button, { onClick: handleButtonClick, className: variant === 'cv' ? 'mc-connect-btn mc-connect-btn--cv' : 'mc-connect-btn', sx: variant === 'cv' ? CV_BUTTON_SX : DEFAULT_BUTTON_SX, children: walletConnection.connectionState === ConnectionState.Connecting ? ('Connecting...') : walletConnection.connectionState === ConnectionState.Connected ? (_jsxs(_Fragment, { children: [_jsx("img", { src: DEFAULT_NETWORK_IMAGES[walletConnection.network], width: variant === 'cv' ? 18 : 25, alt: "", style: variant === 'cv' ? { borderRadius: 0 } : undefined }), _jsxs("div", { style: variant === 'cv' ? { textAlign: 'left', lineHeight: 1.2 } : undefined, children: [_jsx("div", { style: variant === 'cv' ? { fontSize: 12 } : undefined, children: shortenAddress(walletConnection.address) }), _jsx("div", { style: variant === 'cv'
                                            ? { fontSize: 11, color: 'rgba(236, 232, 224, 0.55)', fontWeight: 500 }
                                            : undefined, children: activeState?.isStable
                                            ? `${activeState?.balance} ${activeState?.symbol}`
                                            : `Loading...` })] })] })) : ('Connect Wallet') }), walletConnection.address && (_jsx(Menu, { anchorEl: anchorEl, open: open, onClose: handleClose, slotProps: {
                        paper: {
                            sx: variant === 'cv'
                                ? {
                                    bgcolor: '#100e14',
                                    color: '#ece8e0',
                                    minWidth: '200px',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 106, 26, 0.22)',
                                    boxShadow: '0 0 24px rgba(255, 106, 26, 0.18)',
                                    borderRadius: 0,
                                }
                                : {
                                    bgcolor: 'rgba(11, 29, 56, 0.52)',
                                    color: 'white',
                                    minWidth: '200px',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(23, 163, 182, 0.7)',
                                    boxShadow: '0 0 20px rgba(20, 218, 201, 0.48)',
                                    borderRadius: '2px',
                                },
                        },
                    }, children: _jsxs(_Fragment, { children: [_jsxs(MenuItem, { onClick: copyAddress, children: [_jsx(ContentCopyIcon, { fontSize: "small", sx: { mr: 1 } }), _jsx(Typography, { variant: "body2", children: "Copy Address" })] }), _jsxs(MenuItem, { onClick: () => {
                                    handleDisconnect();
                                    handleClose();
                                }, children: [_jsx(ExitToAppIcon, { fontSize: "small", sx: { mr: 1 } }), _jsx(Typography, { variant: "body2", children: "Disconnect" })] }), switchableNetwork.length ? (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: 'flex' }, children: ['Switch Network for ', _jsx(ChainIcon, { src: installedWallets.find((wallet) => wallet.name == walletConnection.wallet)
                                                    ?.icon, alt: walletConnection.wallet })] }), switchableNetwork.map((chain) => (_jsxs(MenuItem, { onClick: () => switchNetworkClicked(chain), children: [_jsx(ChainIcon, { src: DEFAULT_NETWORK_IMAGES[chain], alt: chain }), _jsx(Typography, { variant: "body2", sx: { ml: 1 }, children: chain })] }, chain)))] })) : (_jsx(_Fragment, {}))] }) }))] }) }));
};
